/**
 * Interview Service API client.
 *
 * All calls go through the api-gateway which adds auth + forwards to
 * interview-service in Azure (France Central).
 *
 * The hook-friendly `useInterviewApi()` wraps these with `useAuth().apiCall`
 * so JWT + retries are handled automatically.
 */

export const API_BASE = 'https://api.cevee.ai';

// ============================================================
// Types (mirror backend SQLAlchemy / Pydantic shapes)
// ============================================================

/** Union of every persona id we still resolve in the backend --
 * includes retired/hidden ones (currently nora / iris / theo) so
 * historical sessions whose persona_id pinned to a retired persona
 * still type-check. The recruiter UI only OFFERS visible personas
 * (currently lara / erik) via the /api/v1/personas endpoint,
 * which filters out hidden=true. */
export type PersonaId = 'mira' | 'lara' | 'nora' | 'iris' | 'theo' | 'erik' | 'niko';

/** Fallback when no persona is picked and the template has none pinned. */
export const DEFAULT_PERSONA_ID: PersonaId = 'lara';

export interface Persona {
  id: PersonaId;
  display_name: string;
  voice: string;
  vibe_de: string;
  vibe_en: string;
  best_for: string[];
  gradient: string;
  tone_hint: string;
  intro_text_de: string;
  intro_text_en: string;
  /** Optional per-persona AI-voice playback speed multiplier.
   * When absent, treat as 1.0. Applied client-side to both live
   * BufferSource playbackRate and the static <audio> preview, so
   * what the candidate hears in the preview matches the live room. */
  voice_speed?: number;
}

export interface PersonasResponse {
  default_persona_id: string;
  personas: Persona[];
}

export interface DpaVersion {
  version: string;
  published_at: number;
  summary_de: string;
  summary_en: string;
  full_text_url_de: string;
  full_text_url_en: string;
  requires_admin_role: boolean;
  subprocessors: string[];
}

export interface DpaCurrentResponse {
  current: DpaVersion;
  all_versions: DpaVersion[];
}

export interface DpaAcceptanceRecord {
  id: string;
  company_id: string;
  accepted_by: string;
  dpa_version: string;
  accepted_at: number;
  ip_address: string | null;
  user_agent: string | null;
  withdrawn_at: number | null;
  is_active: boolean;
}

export interface DpaStatusResponse {
  company_id: string;
  current_dpa_version: string | null;
  dpa_accepted_at: number | null;
  has_active_dpa: boolean;
  history: DpaAcceptanceRecord[];
}

// ============================================================
// Pure fetch helpers (use via useInterviewApi() in components)
// ============================================================

export type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

async function jsonOrThrow<T>(resp: Response, action: string): Promise<T> {
  if (!resp.ok) {
    let detail = `${resp.status} ${resp.statusText}`;
    try {
      const body = await resp.json();
      detail = body.detail || detail;
    } catch {
      // ignore
    }
    throw new Error(`Interview API ${action} failed: ${detail}`);
  }
  return resp.json();
}

export async function fetchPersonas(apiCall: ApiCall): Promise<PersonasResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/personas`),
    'fetchPersonas',
  );
}

export async function fetchCurrentDpa(apiCall: ApiCall): Promise<DpaCurrentResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/dpa/current`),
    'fetchCurrentDpa',
  );
}

export async function fetchDpaStatus(apiCall: ApiCall): Promise<DpaStatusResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/dpa/status`),
    'fetchDpaStatus',
  );
}

export async function acceptDpa(
  apiCall: ApiCall,
  dpaVersion: string,
): Promise<DpaStatusResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/dpa/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dpa_version: dpaVersion }),
    }),
    'acceptDpa',
  );
}

export async function withdrawDpa(
  apiCall: ApiCall,
  reason?: string,
): Promise<DpaStatusResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/dpa/withdraw`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || null }),
    }),
    'withdrawDpa',
  );
}

// ============================================================
// Templates + Questions
// ============================================================

export type TemplateType = 'behavioral' | 'coding' | 'system_design' | 'scenario' | 'technical' | 'custom';
// "student" intentionally broad: Werkstudent:in, Praktikum, Azubi,
// Bachelor/Master Studierende -- one bucket for "first real interview
// in this field".
export type TemplateDifficulty = 'student' | 'junior' | 'mid' | 'senior' | 'staff';
// Functional area filter shown on both the recruiter Template-Modal
// and the consumer Mock library. Keeps the chip set small (8 max).
export type TemplateDomain =
  | 'tech'
  | 'product'
  | 'sales_gtm'
  | 'consulting'
  | 'marketing'
  | 'finance'
  | 'operations'
  | 'skilled_trades'
  | 'customer_service'
  | 'academic'
  | 'general';
/** @deprecated Internal pool metadata only (DSA sampler). Not shown in UI. */
export type QuestionDifficulty = 'easy' | 'medium' | 'hard';
export type QuestionType = 'open' | 'coding' | 'scenario' | 'followup_prompt';

export interface EvaluationCriterion {
  id: string;
  label: string;
  /** Legacy DB seeds used ``name`` instead of ``label``. */
  name?: string;
  description?: string;
  weight: number;
  scale: string;
}

/** Stable id + label for API rows that only have ``name`` (older pool seeds). */
export function normalizeEvaluationCriteria(
  raw: EvaluationCriterion[] | null | undefined,
): EvaluationCriterion[] {
  if (!raw?.length) return [];
  return raw.map((c, i) => {
    const label =
      restoreGermanUmlauts((c.label || c.name || '').trim()) || `Criterion ${i + 1}`;
    const id =
      (c.id || '').trim() ||
      label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '') ||
      `criterion_${i}`;
    const description = c.description ? restoreGermanUmlauts(c.description) : c.description;
    return { ...c, id, label, description, scale: c.scale || '1-5' };
  });
}

/** Resolve a scorecard row label using template criteria (fixes legacy "criterion" rows). */
export function resolveCriterionLabel(
  score: { id?: string; label?: string },
  templateCriteria: EvaluationCriterion[],
  index: number,
): string {
  const raw = (score.label || '').trim();
  if (raw && raw.toLowerCase() !== 'criterion') return restoreGermanUmlauts(raw);
  const norm = normalizeEvaluationCriteria(templateCriteria);
  const byId = norm.find((c) => c.id === score.id);
  if (byId?.label) return byId.label;
  return norm[index]?.label || restoreGermanUmlauts(raw) || score.id || 'Criterion';
}

export interface InterviewQuestion {
  id: string;
  template_id: string;
  question_text: string;
  question_type: QuestionType;
  expected_answer_hints: string | null;
  // Backend column: career level and/or internal pool hardness (easy/medium/hard
  // on DSA rows only — not exposed in the product UI).
  difficulty: TemplateDifficulty | QuestionDifficulty | string | null;
  sort_order: number;
  duration_seconds: number | null;
  code_starter: string | null;
  test_cases: unknown | null;
  tags: string[];
  is_required: boolean;
  created_at: number;
}

/**
 * Pool sampling strategy attached to a template. When non-null, the
 * template's questions are a POOL and the AI picks a balanced subset
 * for each new mock session via app.services.question_sampler.
 */
export interface SamplingStrategy {
  total?: number;
  distinct_areas?: boolean;
  respect_user_difficulty?: boolean;
  exclude_seen_in_last_days?: number;
}

/**
 * Strip naming-convention suffixes from a template title so the UI
 * can show a clean name while a separate "Pool" badge signals what
 * kind of template it is.
 *
 * We strip in this order:
 *   - language hint:        " (DE)" / " (EN)"
 *   - pool naming hints:    " - Cevee Hiring Pool", " - Cevee Pool",
 *                           " - Cevee Practice Pool", " - Cevee Uebungs-Pool",
 *                           " - Cevee Übungs-Pool"
 *   - generic trailing:     " - Pool", " Pool" (last word only)
 *
 * Long-term we want every template to be a pool, at which point the
 * "Pool" suffix in DB titles becomes redundant and these strips
 * become no-ops. Until then we centralize the cleanup so card,
 * detail modal, invite picker and builder all stay consistent.
 */
/** Targeted ASCII-German → umlaut fixes (safe for mixed EN/DE copy). */
/**
 * Targeted ASCII → ä/ö/ü/ß restoration for seeded German content.
 *
 * We deliberately do NOT replace every ae/oe/ue pair (would corrupt
 * English words like "query", "queue", "header", "value"). Instead we
 * list the German stems / full words that actually appear in the
 * seeded interview content + obvious safe-by-construction patterns
 * like the noun ending `-itaet` (always becomes `-ität` in German).
 */
export function restoreGermanUmlauts(t: string | null | undefined): string {
  if (!t) return '';
  if (!/(Uebung|fuehr|gefuehr|naech|erklaer|schaetz|pruef|gewaehl|wuerd|koenn|groes|Loes|loes|fuer|ueber|Gespraech|Kaeufer|Kapazitaet|Fehlerfaelle|unterstuetz|Erzaehl|itaet|itaeten|haett|haeufig|haeufigsten|moegli|noeti|moglichst|gewoehn|verfueg|Verfueg|stillstaen|verstaen|anwaelt|diaet|schueler|faecher|jaehri|waehrend|begruend|begruess|ansaetz|klaerung|marktueblich|saetzen|erlaeut|ploetzli|naehe|ausgewael|aufklaer|nuetzli|tatsaechlich|forschungs|kommiliton|Problemloesung|Lernfaehigkeit|faehigkeit|Staer|staer|plaetz|hoer|natuer|Lueck|lueck|geprae|abschl|Allgemeinplaet)/i.test(t)) {
    return t;
  }
  return t
    .replace(/\bUebungs-Pool\b/gi, 'Übungs-Pool')
    .replace(/\bUebungs\b/g, 'Übungs')
    .replace(/\buebungs\b/g, 'übungs')
    .replace(/\bUebung\b/g, 'Übung')
    .replace(/\buebung\b/g, 'übung')
    .replace(/\bGroesste\b/g, 'Größte')
    .replace(/\bgroesste\b/g, 'größte')
    .replace(/\bGroesser\b/g, 'Größer')
    .replace(/\bgroesser\b/g, 'größer')
    .replace(/\bAusser\b/g, 'Außer')
    .replace(/\bausser\b/g, 'außer')
    .replace(/gefuehr/gi, (m) => (m[0] === 'G' ? 'Geführ' : 'geführ'))
    .replace(/fuehr/gi, (m) => (m[0] === 'F' ? 'Führ' : 'führ'))
    .replace(/hoer/gi, (m) => (m[0] === 'H' ? 'Hör' : 'hör'))
    .replace(/Staer/g, 'Stär')
    .replace(/staer/g, 'stär')
    .replace(/plaetz/g, 'plätz')
    .replace(/Plaetz/g, 'Plätz')
    .replace(/natuer/g, 'natür')
    .replace(/Natuer/g, 'Natür')
    .replace(/Lueck/g, 'Lück')
    .replace(/lueck/g, 'lück')
    .replace(/geprae/g, 'geprä')
    .replace(/Geprae/g, 'Geprä')
    .replace(/schli/g, 'schließ')
    .replace(/Schli/g, 'Schließ')
    .replace(/naech/gi, (m) => (m[0] === 'N' ? 'Näch' : 'näch'))
    .replace(/erklaer/gi, (m) => (m[0] === 'E' ? 'Erklär' : 'erklär'))
    .replace(/schaetz/gi, (m) => (m[0] === 'S' ? 'Schätz' : 'schätz'))
    .replace(/pruef/gi, (m) => (m[0] === 'P' ? 'Prüf' : 'prüf'))
    .replace(/gewaehl/gi, (m) => (m[0] === 'G' ? 'Gewähl' : 'gewähl'))
    .replace(/wuerd/gi, (m) => (m[0] === 'W' ? 'Würd' : 'würd'))
    .replace(/koenn/gi, (m) => (m[0] === 'K' ? 'Könn' : 'könn'))
    .replace(/groes/gi, (m) => (m[0] === 'G' ? 'Größ' : 'größ'))
    .replace(/Loes/g, 'Lös')
    .replace(/loes/g, 'lös')
    .replace(/\bfuer\b/gi, (m) => (m[0] === 'F' ? 'Für' : 'für'))
    .replace(/\bueber\b/gi, (m) => (m[0] === 'U' ? 'Über' : 'über'))
    .replace(/Gespraech/gi, (m) => (m[0] === 'G' ? 'Gespräch' : 'gespräch'))
    .replace(/Gespraeche/g, 'Gespräche')
    .replace(/gespraeche/g, 'gespräche')
    .replace(/Kaeufer/g, 'Käufer')
    .replace(/kaeufer/g, 'käufer')
    .replace(/Lernfaehigkeit/g, 'Lernfähigkeit')
    .replace(/Problemloesung/g, 'Problemlösung')
    .replace(/vollstaendig/g, 'vollständig')
    .replace(/Problemverstaendnis/g, 'Problemverständnis')
    .replace(/Problemloesen/g, 'Problemlösen')
    .replace(/Kapazitaet/g, 'Kapazität')
    .replace(/Fehlerfaelle/g, 'Fehlerfälle')
    .replace(/unterstuetzend/g, 'unterstützend')
    .replace(/Erzaehl/g, 'Erzähl')
    .replace(/erzaehl/g, 'erzähl')
    .replace(/Metriken-Uebung/g, 'Metriken-Übung')
    // ── Generic German noun ending: -itaet → -ität ──────────────
    // Sensitivitaet, Sensibilitaet, Qualitaet, Komplexitaet,
    // Kreativitaet, Saisonalitaet, Plausibilitaet, Attraktivitaet,
    // Optionalitaet, Defensivitaet, Nichtlinearitaet, Realitaet, …
    .replace(/itaeten\b/g, 'itäten')
    .replace(/itaet\b/g, 'ität')
    // ── ä-stems ──────────────────────────────────────────────────
    .replace(/haetten\b/g, 'hätten')
    .replace(/haettest\b/g, 'hättest')
    .replace(/haette\b/g, 'hätte')
    .replace(/haeufigsten/g, 'häufigsten')
    .replace(/haeufig/g, 'häufig')
    .replace(/Haeufig/g, 'Häufig')
    .replace(/Stillstaende/g, 'Stillstände')
    .replace(/Verstaendnis/g, 'Verständnis')
    .replace(/verstaendni/g, 'verständni')
    .replace(/Anwaelt/g, 'Anwält')
    .replace(/anwaelt/g, 'anwält')
    .replace(/Diaet/g, 'Diät')
    .replace(/diaet/g, 'diät')
    .replace(/Schueler/g, 'Schüler')
    .replace(/schueler/g, 'schüler')
    .replace(/Faecher/g, 'Fächer')
    .replace(/faecher/g, 'fächer')
    .replace(/jaehrig/gi, (m) => (m[0] === 'J' ? 'Jährig' : 'jährig'))
    .replace(/jaehrli/gi, (m) => (m[0] === 'J' ? 'Jährli' : 'jährli'))
    .replace(/waehrend/g, 'während')
    .replace(/Waehrend/g, 'Während')
    .replace(/waehl/gi, (m) => (m[0] === 'W' ? 'Wähl' : 'wähl'))
    .replace(/begruend/gi, (m) => (m[0] === 'B' ? 'Begründ' : 'begründ'))
    .replace(/begruess/gi, (m) => (m[0] === 'B' ? 'Begrüß' : 'begrüß'))
    .replace(/ansaetz/g, 'ansätz')
    .replace(/Ansaetz/g, 'Ansätz')
    .replace(/klaerung/g, 'klärung')
    .replace(/Klaerung/g, 'Klärung')
    .replace(/aufklaer/g, 'aufklär')
    .replace(/Aufklaer/g, 'Aufklär')
    .replace(/Saetzen/g, 'Sätzen')
    .replace(/saetzen/g, 'sätzen')
    .replace(/erlaeut/g, 'erläut')
    .replace(/Erlaeut/g, 'Erläut')
    .replace(/marktueblich/g, 'marktüblich')
    .replace(/Marktueblich/g, 'Marktüblich')
    .replace(/ueblich/g, 'üblich')
    .replace(/Ueblich/g, 'Üblich')
    .replace(/tatsaechlich/g, 'tatsächlich')
    .replace(/Tatsaechlich/g, 'Tatsächlich')
    .replace(/aehnlich/g, 'ähnlich')
    .replace(/Aehnlich/g, 'Ähnlich')
    .replace(/Auswael/g, 'Auswähl')
    .replace(/auswael/g, 'auswähl')
    .replace(/ausgewael/g, 'ausgewähl')
    .replace(/Ausgewael/g, 'Ausgewähl')
    // ── ö-stems ──────────────────────────────────────────────────
    .replace(/moegli/g, 'mögli')
    .replace(/Moegli/g, 'Mögli')
    .replace(/moeglichst/g, 'möglichst')
    .replace(/noeti/g, 'nöti')
    .replace(/Noeti/g, 'Nöti')
    .replace(/ploetzli/g, 'plötzli')
    .replace(/Ploetzli/g, 'Plötzli')
    .replace(/gewoehn/g, 'gewöhn')
    .replace(/Gewoehn/g, 'Gewöhn')
    // ── ü-stems ──────────────────────────────────────────────────
    .replace(/verfueg/g, 'verfüg')
    .replace(/Verfueg/g, 'Verfüg')
    .replace(/nuetzli/g, 'nützli')
    .replace(/Nuetzli/g, 'Nützli');
}

/** Normalize to ASCII hyphen (no --, no em/en dash). */
export function sanitizeInterviewCopy(t: string | null | undefined): string {
  if (!t) return '';
  return restoreGermanUmlauts(
    t
    .replace(/\b(Step|Schritt)\s+(\d+),\s*/g, '$1 $2 - ')
    .replace(/\b(Step|Schritt)\s+(\d+):\s*/g, '$1 $2 - ')
    .replace(/\s*—\s*/g, ' - ')
    .replace(/\u2013/g, '-')
    .replace(/\s+--\s+/g, ' - ')
    .replace(/(?<![-])--(?![-])/g, '-')
    .replace(/ - - /g, ' - ')
    .replace(/\s{2,}/g, ' ')
    .trim(),
  );
}

export function cleanTemplateTitle(t: string): string {
  if (!t) return t;
  let out = t;
  out = out.replace(/\s*\((DE|EN)\)\s*$/i, '');
  // Fix common acronym casing in display titles
  out = out.replace(/\bAi\b/g, 'AI').replace(/\bOo\b/g, 'Object-Oriented');
  out = out.replace(/\bApi\b/g, 'API').replace(/\bGtm\b/g, 'GTM').replace(/\bPm\b/g, 'PM');
  out = out.replace(/\bOo\s+/g, 'Object-Oriented ').replace(/\bAi\s+/g, 'AI ');
  out = out.replace(/\bApi\s+/g, 'API ').replace(/\bGtm\s+/g, 'GTM ').replace(/\bPm\s+/g, 'PM ');
  out = out.replace(
    /\s*[-–—]?\s*Cevee\s+(Hiring\s+Pool|Practice\s+Pool|Übungs[- ]?Pool|Uebungs[- ]?Pool|Übungs\s+Pool|Uebungs\s+Pool|Hiring\s+Pool|Pool)\s*$/i,
    '',
  );
  out = out.replace(/\s*[-–—]\s*Pool\s*$/i, '');
  // Legacy 040 titles: "Ai Fullstack Engineer Cevee Hiring Pool" (no dash)
  out = out.replace(
    /\s+Cevee\s+(Hiring\s+Pool|Practice\s+Pool|Übungs[- ]?Pool|Uebungs[- ]?Pool|Übungs\s+Pool|Uebungs\s+Pool|Hiring\s+Pool|Pool)\s*$/i,
    '',
  );
  // Drop trailing " Hiring" / " Practice" / " Übung*". The mock vs
  // recruiter surface already disambiguates which pool variant the
  // user is looking at, so the suffix is redundant in the title.
  out = out.replace(/\s+(Hiring|Practice|Übung|Uebung|Übungs|Uebungs)\s*$/i, '');
  return restoreGermanUmlauts(sanitizeInterviewCopy(out));
}

export interface InterviewTemplate {
  id: string;
  company_id: string | null;
  title: string;
  description: string | null;
  type: TemplateType;
  difficulty: TemplateDifficulty | null;
  domain: TemplateDomain | null;
  duration_minutes: number;
  language: string;
  family_key: string | null;
  enable_coding_panel: boolean;
  enable_whiteboard_panel: boolean;
  enable_numbers_panel: boolean;
  enable_notepad_panel: boolean;
  enable_screen_share: boolean;
  available_for_practice: boolean;
  available_for_recruiter: boolean;
  evaluation_criteria: EvaluationCriterion[];
  is_public: boolean;
  is_active: boolean;
  /** Cevee-curated, score-comparable Pre-Screen template. UI shows a badge. */
  is_cevee_official?: boolean;
  persona_id: PersonaId;
  created_by: string | null;
  created_at: number;
  updated_at: number;
  questions: InterviewQuestion[];
  /**
   * When non-null this template is a question POOL. The UI uses this
   * to render a "pool" summary instead of listing every question.
   */
  sampling_strategy?: SamplingStrategy | null;
  /** Library API: typical steps when one pool case is sampled. */
  steps_per_session?: number | null;
}

export interface TemplateCreatePayload {
  title: string;
  description?: string;
  type: TemplateType;
  difficulty?: TemplateDifficulty;
  domain?: TemplateDomain;
  duration_minutes: number;
  language: string;
  persona_id: PersonaId;
  enable_coding_panel?: boolean;
  enable_whiteboard_panel?: boolean;
  enable_numbers_panel?: boolean;
  enable_notepad_panel?: boolean;
  enable_screen_share?: boolean;
  evaluation_criteria: EvaluationCriterion[];
  questions: Array<{
    question_text: string;
    question_type: QuestionType;
    expected_answer_hints?: string;
    difficulty?: TemplateDifficulty | QuestionDifficulty | string;
    sort_order: number;
    duration_seconds?: number;
    tags?: string[];
    is_required: boolean;
  }>;
}

export async function listTemplates(
  apiCall: ApiCall,
  opts: {
    type?: TemplateType;
    difficulty?: TemplateDifficulty;
    domain?: TemplateDomain;
    includePublic?: boolean;
    onlyActive?: boolean;
  } = {},
): Promise<{ templates: InterviewTemplate[] }> {
  const qs = new URLSearchParams();
  qs.set('include_public', String(opts.includePublic ?? true));
  qs.set('only_active', String(opts.onlyActive ?? true));
  if (opts.type) qs.set('type', opts.type);
  if (opts.difficulty) qs.set('difficulty', opts.difficulty);
  if (opts.domain) qs.set('domain', opts.domain);
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates?${qs.toString()}`),
    'listTemplates',
  );
}

export async function getTemplate(
  apiCall: ApiCall,
  templateId: string,
): Promise<InterviewTemplate> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates/${templateId}`),
    'getTemplate',
  );
}

export async function createTemplate(
  apiCall: ApiCall,
  payload: TemplateCreatePayload,
): Promise<InterviewTemplate> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'createTemplate',
  );
}

/**
 * Update an existing template. Accepts the same shape as create; the backend
 * applies provided fields and (for non-pool templates) replaces the question
 * list when `questions` is included.
 */
export async function updateTemplate(
  apiCall: ApiCall,
  templateId: string,
  payload: Partial<TemplateCreatePayload>,
): Promise<InterviewTemplate> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates/${templateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'updateTemplate',
  );
}

/**
 * Agent 1 (Template Architect) output: a recommended single-interview
 * template drafted from a job description. Pure suggestion -- the recruiter
 * reviews/edits it in the builder before anything is saved. Persona is
 * intentionally NOT suggested (recruiter's choice).
 */
export interface TemplateDraft {
  title: string;
  description: string;
  language: 'de' | 'en';
  type: TemplateType;
  difficulty: TemplateDifficulty;
  domain: TemplateDomain;
  duration_minutes: number;
  enable_coding_panel: boolean;
  enable_whiteboard_panel: boolean;
  enable_numbers_panel: boolean;
  enable_notepad_panel: boolean;
  evaluation_criteria: EvaluationCriterion[];
  questions: Array<{
    question_text: string;
    expected_answer_hints: string;
    question_type: QuestionType;
    duration_seconds: number;
    sort_order: number;
  }>;
}

export async function draftTemplateFromJd(
  apiCall: ApiCall,
  opts: { jobDescription: string; language?: string },
): Promise<TemplateDraft> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates/draft-from-jd`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        job_description: opts.jobDescription,
        language: opts.language,
      }),
    }),
    'draftTemplateFromJd',
  );
}

/**
 * Extract text from an uploaded job description file (PDF/PNG/JPG) via the
 * existing recruiter OCR endpoint (PyPDF2 for PDFs, Tesseract for images).
 * Reused by the template builder's "draft from JD" flow.
 */
export async function extractJobText(apiCall: ApiCall, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const data = await jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/companies/extract-job-text`, {
      method: 'POST',
      body: fd,
    }),
    'extractJobText',
  );
  return typeof (data as { text?: unknown })?.text === 'string' ? (data as { text: string }).text : '';
}

/**
 * Phase 2: enqueue async pool generation (Agent 2) for an existing template.
 * `append` keeps an existing pool and adds more distinct cases; otherwise the
 * pool is (re)built from scratch.
 */
export async function generateTemplatePool(
  apiCall: ApiCall,
  templateId: string,
  nCases: number,
  append = false,
): Promise<{ status: string; template_id: string; n_cases: number }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates/${templateId}/generate-pool`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ n_cases: nCases, append }),
    }),
    'generateTemplatePool',
  );
}

export interface PoolGenStatus {
  status: 'idle' | 'queued' | 'running' | 'done' | 'failed';
  cases?: number;
  steps?: number;
  /** Live progress during generation: cases produced so far / target total. */
  generated?: number;
  n_cases?: number;
  error?: string;
  family_key?: string;
}

export async function getPoolStatus(
  apiCall: ApiCall,
  templateId: string,
): Promise<PoolGenStatus> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates/${templateId}/pool-status`),
    'getPoolStatus',
  );
}

export async function deleteTemplate(
  apiCall: ApiCall,
  templateId: string,
): Promise<{ id: string; deleted: boolean }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/templates/${templateId}`, {
      method: 'DELETE',
    }),
    'deleteTemplate',
  );
}

// ============================================================
// Invites
// ============================================================

export type InviteStatus =
  | 'invited'
  | 'account_linked'
  | 'consent_given'
  | 'started'
  | 'in_progress'
  | 'completed'
  | 'expired'
  | 'cancelled';

export type SeniorityLevel =
  | 'intern'
  | 'junior'
  | 'mid'
  | 'senior'
  | 'staff'
  | 'principal'
  | 'lead';

export interface InviteSession {
  id: string;
  template_id: string;
  candidate_id: string | null;
  invited_email?: string;
  created_by_recruiter_id: string | null;
  company_id: string | null;
  status: InviteStatus;
  persona_id: PersonaId;
  invite_expires_at: number | null;
  invite_sent_at: number | null;
  visibility_scope: string;
  role_title: string;
  role_seniority: SeniorityLevel | null;
  role_description: string | null;
  started_at: number | null;
  completed_at: number | null;
  duration_seconds: number | null;
  /** Self-served mock session (no recruiter, candidate=user themselves). */
  is_mock?: boolean;
  /** Optional ATS link (e.g. Ashby job id) chosen during invite creation. */
  ats_job_id?: string | null;
  ats_provider?: string | null;
  created_at: number;
  updated_at: number;
}

export interface InviteCreateResponse {
  session: InviteSession;
  raw_token: string;
  invite_url: string;
}

export interface InviteCreatePayload {
  template_id: string;
  invited_email: string;
  role_title: string;
  role_seniority?: SeniorityLevel | null;
  role_description?: string | null;
  persona_id?: PersonaId;
  invite_message?: string;
  expires_in_hours?: number;
  /** Pool templates: recruiter picks 30 / 45 / 60 min (like mock start). */
  target_duration_minutes?: 15 | 30 | 45 | 60;
  visibility_scope?: 'creator_only' | 'company' | 'shared';
  /** Optional ATS link — set when the recruiter ties this invite to a job opening. */
  ats_job_id?: string | null;
  ats_provider?: string | null;
}

export interface InvitePublicLanding {
  session_id: string;
  status: InviteStatus;
  invited_email: string;
  invite_expires_at: number | null;
  invite_message: string | null;
  role_title: string;
  role_seniority: SeniorityLevel | null;
  template: {
    id: string;
    title: string;
    type: TemplateType;
    duration_minutes: number;
    language: string;
    persona_id: string;
    enable_coding_panel?: boolean;
    enable_whiteboard_panel?: boolean;
    enable_numbers_panel?: boolean;
    enable_notepad_panel?: boolean;
    enable_screen_share?: boolean;
  };
  company_name: string | null;
  persona: {
    id: string;
    name: string;
    voice: string;
    gradient: string;
    personality_short: string;
    voice_speed?: number | null;
  } | null;
  is_expired: boolean;
  is_accepted: boolean;
  is_mock?: boolean;
  /** Recruiter previewing their own template (is_mock=true + demo source). */
  is_recruiter_demo?: boolean;
  /** Passport pre-screen (is_mock=true + passport_prescreen source). */
  is_prescreen?: boolean;
  /** Lite-ATS warm-intro: candidate can take the interview without login. */
  anonymous?: boolean;
  requires_login: boolean;
}

export interface InviteAcceptPayload {
  consent_recording: boolean;
  consent_ai_evaluation: boolean;
  consent_locale?: string;
}

export async function listInvites(
  apiCall: ApiCall,
  opts: { status?: InviteStatus; mineOnly?: boolean; limit?: number } = {},
): Promise<{ invites: InviteSession[] }> {
  const qs = new URLSearchParams();
  qs.set('mine_only', String(opts.mineOnly ?? false));
  qs.set('limit', String(opts.limit ?? 100));
  if (opts.status) qs.set('status', opts.status);
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites?${qs.toString()}`),
    'listInvites',
  );
}

export async function createInvite(
  apiCall: ApiCall,
  payload: InviteCreatePayload,
): Promise<InviteCreateResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'createInvite',
  );
}

export async function cancelInvite(
  apiCall: ApiCall,
  sessionId: string,
  reason?: string,
): Promise<{ id: string; status: string }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites/${sessionId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || null }),
    }),
    'cancelInvite',
  );
}

export async function resendInvite(
  apiCall: ApiCall,
  sessionId: string,
  extendExpiryHours?: number,
): Promise<InviteCreateResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites/${sessionId}/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ extend_expiry_hours: extendExpiryHours || null }),
    }),
    'resendInvite',
  );
}

/**
 * Anonymous resolution for the candidate landing page.
 * Uses native fetch (no auth) -- whitelisted at the gateway.
 */
export async function resolveInvitePublic(rawToken: string): Promise<InvitePublicLanding> {
  const resp = await fetch(
    `${API_BASE}/api/v1/interview/invites/resolve/${encodeURIComponent(rawToken)}`,
  );
  if (!resp.ok) {
    let detail = `${resp.status} ${resp.statusText}`;
    try {
      const body = await resp.json();
      detail = body.detail?.message || body.detail || detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return resp.json();
}

export async function acceptInvite(
  apiCall: ApiCall,
  rawToken: string,
  payload: InviteAcceptPayload,
): Promise<InviteSession> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites/accept/${encodeURIComponent(rawToken)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        consent_locale: 'de',
        ...payload,
      }),
    }),
    'acceptInvite',
  );
}

/**
 * Anonymous (no-login) consent for a Lite-ATS warm-intro invite.
 * Uses native fetch -- whitelisted at the gateway, token-gated at the backend.
 */
export async function acceptInviteAnonymous(
  rawToken: string,
  payload: InviteAcceptPayload,
): Promise<{ session_id: string; status: string }> {
  const resp = await fetch(
    `${API_BASE}/api/v1/interview/invites/accept-anon/${encodeURIComponent(rawToken)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consent_locale: 'de', ...payload }),
    },
  );
  return jsonOrThrow(resp, 'acceptInviteAnonymous');
}

/** Anonymous candidate session context (no JWT) -- mirrors fetchMySession. */
export async function fetchSessionByToken(rawToken: string): Promise<CandidateSessionMe> {
  const resp = await fetch(
    `${API_BASE}/api/v1/interview/sessions/by-token/${encodeURIComponent(rawToken)}/me`,
  );
  return jsonOrThrow(resp, 'fetchSessionByToken');
}

/** Anonymous start -- mints resume_token without a Cevee account. */
export async function startSessionByToken(rawToken: string): Promise<SessionStartResponse> {
  const resp = await fetch(
    `${API_BASE}/api/v1/interview/sessions/by-token/${encodeURIComponent(rawToken)}/start`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  return jsonOrThrow(resp, 'startSessionByToken');
}

/** Anonymous complete. */
export async function completeSessionByToken(rawToken: string): Promise<SessionCompleteResponse> {
  const resp = await fetch(
    `${API_BASE}/api/v1/interview/sessions/by-token/${encodeURIComponent(rawToken)}/complete`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  return jsonOrThrow(resp, 'completeSessionByToken');
}

// ============================================================
// Sessions (candidate runtime)
// ============================================================

/** Recruiter preview run (is_mock + consent source=recruiter_demo). */
export function isRecruiterDemoSession(
  session: {
    is_mock?: boolean;
    candidate_consent_scope?: Record<string, unknown> | null;
  } | null | undefined,
): boolean {
  if (!session?.is_mock) return false;
  return session.candidate_consent_scope?.source === 'recruiter_demo';
}

/**
 * Passport pre-screen run (is_mock + consent source=passport_prescreen).
 *
 * A pre-screen rides the mock plumbing (is_mock=true) but is NOT a private
 * practice simulation: the candidate runs it on themselves from their Passport
 * and can choose to share the result with recruiters. UI must frame it as a
 * "Pre-Screen", not a "Simulation".
 */
export function isPreScreenSession(
  session: {
    is_mock?: boolean;
    candidate_consent_scope?: Record<string, unknown> | null;
  } | null | undefined,
): boolean {
  if (!session?.is_mock) return false;
  return session.candidate_consent_scope?.source === 'passport_prescreen';
}

export interface CandidateSessionMe {
  session: InviteSession;
  /** Set by GET /sessions/{id}/me when this is a recruiter demo run. */
  is_recruiter_demo?: boolean;
  template: {
    id: string;
    title: string;
    type: TemplateType;
    duration_minutes: number;
    language: string;
    persona_id: string;
    family_key?: string | null;
    enable_coding_panel?: boolean;
    enable_whiteboard_panel?: boolean;
    enable_numbers_panel?: boolean;
    enable_notepad_panel?: boolean;
    enable_screen_share?: boolean;
  } | null;
  company_name: string | null;
  persona: {
    id: string;
    name: string;
    voice: string;
    gradient: string;
    voice_speed?: number | null;
  };
}

export interface SessionStartResponse {
  session: InviteSession;
  resume_token: string;
  ws_url: string;            // path-only, e.g. /api/v1/sessions/{id}/ws
  persona: {
    id: string;
    name: string;
    voice: string;
    gradient: string;
    voice_speed?: number | null;
  };
  language: string;
  duration_minutes: number;
}

export interface SessionCompleteResponse {
  session: InviteSession;
  already_completed: boolean;
}

export async function fetchMySession(
  apiCall: ApiCall,
  sessionId: string,
): Promise<CandidateSessionMe> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/sessions/${sessionId}/me`),
    'fetchMySession',
  );
}

export async function startSession(
  apiCall: ApiCall,
  sessionId: string,
): Promise<SessionStartResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/sessions/${sessionId}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),
    'startSession',
  );
}

export async function completeSession(
  apiCall: ApiCall,
  sessionId: string,
): Promise<SessionCompleteResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/sessions/${sessionId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),
    'completeSession',
  );
}

// ============================================================
// Results (recruiter review)
// ============================================================

export type Recommendation = 'strong_hire' | 'hire' | 'neutral' | 'no_hire' | 'strong_no_hire';

export interface CriterionScore {
  id: string;
  label: string;
  score: number; // 1..5
  weight: number;
  justification: string;
  evidence: string[];
}

export interface InterviewEvaluation {
  id: string;
  session_id: string;
  overall_score: number | null;
  recommendation: Recommendation | null;
  criteria_scores: CriterionScore[] | null;
  strengths: string | null;
  weaknesses: string | null;
  detailed_feedback: string | null;
  explanation_text: string | null;
  ai_confidence: number | null;
  recruiter_reviewed_at: number | null;
  recruiter_override_score: number | null;
  recruiter_override_recommendation: Recommendation | null;
  recruiter_notes: string | null;
  final_score: number | null;
  final_recommendation: Recommendation | null;
  is_reviewed: boolean;
  created_at: number;
  updated_at: number;
}

export interface ResultSummary {
  session_id: string;
  candidate_id: string | null;
  candidate_label: string;
  invited_email: string;
  template_id: string;
  template_title: string | null;
  template_type: TemplateType | null;
  role_title: string;
  role_seniority: SeniorityLevel | null;
  persona_id: string;
  status: InviteStatus;
  started_at: number | null;
  completed_at: number | null;
  duration_seconds: number | null;
  has_recording: boolean;
  safety_flag_count: number;
  overall_score: number | null;
  recommendation: Recommendation | null;
  final_score: number | null;
  final_recommendation: Recommendation | null;
  is_reviewed: boolean;
  ai_confidence: number | null;
  created_by_recruiter_id: string;
  created_at: number;
  updated_at: number;
  /**
   * Candidate's short self-reported intake (get-to-know pre-screen only):
   * where they're based / want to go / what they're looking for. Null when not
   * provided. Session-scoped (never read from the profile).
   */
  intake?: PreScreenIntake | null;
}

export interface TranscriptLine {
  ts_ms: number;
  speaker: 'ai' | 'candidate' | 'system';
  text: string;
}

export interface SafetyFlag {
  category: 'prompt_injection' | 'forbidden_topic' | string;
  actor: 'candidate' | 'ai' | 'system';
  ts_ms: number;
  rule: string;
  snippet: string;
}

export interface AuditLogEntry {
  id: string;
  session_id: string;
  actor_id: string | null;
  actor_type: string | null;
  action: string;
  details: Record<string, unknown> | null;
  created_at: number;
}

export interface ResultDetail {
  summary: ResultSummary;
  recording_url: string | null;
  /**
   * Screen-capture playback URL. Populated when the session was recorded
   * in dual-stream mode (cam + screen). Null for legacy single-stream
   * recordings and for sessions where the candidate never shared their
   * screen. Both URLs are short-lived SAS reads (60 min TTL).
   */
  recording_url_screen?: string | null;
  session: InviteSession & {
    invited_email: string;
    transcript: TranscriptLine[] | null;
    recording_blob_url: string | null;
    recording_blob_url_screen?: string | null;
    realtime_session_id: string | null;
    candidate_consent_given_at: number | null;
    candidate_consent_scope: Record<string, unknown> | null;
    data_retention_until: number | null;
    safety_flags: SafetyFlag[];
    code_submissions: {
      language?: string;
      content?: string;
      char_count?: number;
      updated_at_ms?: number;
    } | null;
    canvas_submissions: {
      kind?: string;
      scene?: unknown;
      summary?: string;
      element_count?: number;
      updated_at_ms?: number;
    } | null;
    numbers_submissions: {
      rows?: number;
      cols?: number;
      cells?: Record<string, { raw?: string; value?: string | number | null }>;
      summary?: string;
      cell_count?: number;
      updated_at_ms?: number;
    } | null;
    notepad_submissions: {
      content?: string;
      char_count?: number;
      updated_at_ms?: number;
    } | null;
  };
  template: {
    id: string;
    title: string;
    type: TemplateType;
    duration_minutes: number;
    language: string;
    persona_id: string;
    evaluation_criteria: EvaluationCriterion[];
    enable_coding_panel?: boolean;
    enable_whiteboard_panel?: boolean;
    enable_numbers_panel?: boolean;
    enable_notepad_panel?: boolean;
    enable_screen_share?: boolean;
  } | null;
  evaluation: InterviewEvaluation | null;
  audit: AuditLogEntry[];
}

export interface ReviewPayload {
  override_recommendation?: Recommendation | null;
  override_score?: number | null;
  notes?: string | null;
}

export async function listResults(
  apiCall: ApiCall,
  opts: { onlyMine?: boolean; status?: InviteStatus; limit?: number } = {},
): Promise<{ results: ResultSummary[] }> {
  const qs = new URLSearchParams();
  if (opts.onlyMine !== undefined) qs.set('only_mine', String(opts.onlyMine));
  if (opts.status) qs.set('status', opts.status);
  if (opts.limit) qs.set('limit', String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/results${suffix}`),
    'listResults',
  );
}

export async function getResult(
  apiCall: ApiCall,
  sessionId: string,
): Promise<ResultDetail> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/results/${sessionId}`),
    'getResult',
  );
}

export async function downloadRecordingMp4(
  apiCall: ApiCall,
  sessionId: string,
  kind: 'cam' | 'screen',
): Promise<Blob> {
  const res = await apiCall(
    `${API_BASE}/api/v1/interview/results/${sessionId}/recording/download?kind=${kind}&format=mp4`,
  );
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail?.message || body?.detail?.code || body?.detail || detail;
    } catch {
      detail = (await res.text().catch(() => detail)) || detail;
    }
    throw new Error(String(detail));
  }
  return res.blob();
}

export async function submitReview(
  apiCall: ApiCall,
  sessionId: string,
  payload: ReviewPayload,
): Promise<{ evaluation: InterviewEvaluation }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/results/${sessionId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'submitReview',
  );
}

// ============================================================
// Mock (consumer-side AI Mock Interviews, admin-guarded in Sprint A)
// ============================================================

export interface MockQuota {
  total: number;
  used: number;
  remaining: number;
  period_start: number;
  period_end: number;
  tier: 'free' | 'premium' | 'star' | 'admin' | string;
  is_admin: boolean;
}

export interface MockLibraryTemplate {
  id: string;
  title: string;
  description: string | null;
  type: TemplateType | null;
  difficulty: string | null;
  domain: TemplateDomain | null;
  duration_minutes: number;
  language: string;
  family_key: string | null;
  persona_id: string;
  enable_coding_panel: boolean;
  enable_whiteboard_panel: boolean;
  enable_numbers_panel: boolean;
  enable_notepad_panel: boolean;
  enable_screen_share: boolean;
  /** True when the template carries a sampling_strategy. */
  is_pool: boolean;
  sampling_strategy?: SamplingStrategy | null;
  /** Typical steps in one sampled interview (one pool case). */
  steps_per_session?: number | null;
  /** practice = Übungspool on /mock; hiring = recruiter-only sibling pool. */
  pool_side?: 'practice' | 'hiring';
}

export interface MockHistoryItem {
  session_id: string;
  template_title: string | null;
  template_id: string;
  template_type: TemplateType | null;
  difficulty: string | null;
  language: string | null;
  status: string;
  started_at: number | null;
  completed_at: number | null;
  duration_seconds: number | null;
  persona_id: string;
  overall_score: number | null;
  recommendation: string | null;
  ai_confidence: number | null;
  created_at: number;
}

export interface MockStartPayload {
  template_id: string;
  persona_id?: string | null;
  role_title?: string | null;
  role_seniority?: SeniorityLevel | null;
  role_description?: string | null;
  language?: string | null;
  /**
   * Candidate-selected career level. Drives system-prompt tone, hint
   * generosity, and task rigour. Server accepts the canonical 4-tier
   * axis. Legacy easy/medium/hard from old clients are aliased server-side.
   */
  /** Candidate-selected career level (required for mock start). */
  target_difficulty: 'student' | 'junior' | 'mid' | 'senior';
  /** Candidate-selected duration in minutes (required for mock start). */
  target_duration_minutes: number;
  /** Required: must be true. Frontend collects via the consent modal. */
  explicit_consent: boolean;
}

export interface MockStartResponse {
  session: InviteSession;
  raw_token: string;
  invite_url: string;
  quota: MockQuota;
}

export async function getMockQuota(apiCall: ApiCall): Promise<{ quota: MockQuota }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/quota`),
    'getMockQuota',
  );
}

export async function listMockLibrary(
  apiCall: ApiCall,
  opts: {
    language?: string;
    type?: TemplateType;
    difficulty?: string;
    domain?: TemplateDomain;
  } = {},
): Promise<{ templates: MockLibraryTemplate[] }> {
  const qs = new URLSearchParams();
  if (opts.language) qs.set('language', opts.language);
  if (opts.type) qs.set('type', opts.type);
  if (opts.difficulty) qs.set('difficulty', opts.difficulty);
  if (opts.domain) qs.set('domain', opts.domain);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/library${suffix}`),
    'listMockLibrary',
  );
}

export async function startMock(
  apiCall: ApiCall,
  payload: MockStartPayload,
): Promise<MockStartResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'startMock',
  );
}

export interface MockStartCustomPayload {
  topic: string;
  type: 'behavioral' | 'coding' | 'system_design' | 'technical' | 'scenario';
  difficulty: 'student' | 'junior' | 'mid' | 'senior' | 'staff';
  duration_minutes: 15 | 30 | 45 | 60;
  language: 'de' | 'en';
  persona_id?: string | null;
  enable_coding_panel?: boolean;
  enable_whiteboard_panel?: boolean;
  enable_numbers_panel?: boolean;
  enable_notepad_panel?: boolean;
  explicit_consent: boolean;
}

export async function startCustomMock(
  apiCall: ApiCall,
  payload: MockStartCustomPayload,
): Promise<MockStartResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/start-custom`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'startCustomMock',
  );
}

export async function listMockHistory(
  apiCall: ApiCall,
  limit: number = 50,
): Promise<{ history: MockHistoryItem[] }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/history?limit=${limit}`),
    'listMockHistory',
  );
}

/**
 * Detail view for a single mock session owned by the calling user.
 *
 * Returns the same shape as the recruiter-side `getResult()` so we can
 * reuse the panel components, with two consumer-side differences:
 *  - `evaluation.explanation_text` is intentionally stripped (consumer
 *    sees the direct detailed_feedback, no softened restatement).
 *  - audit log entries are filtered to the candidate's own actions.
 */
export async function getMockResult(
  apiCall: ApiCall,
  sessionId: string,
): Promise<ResultDetail> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/sessions/${sessionId}`),
    'getMockResult',
  );
}

/**
 * Recruiter-gated full detail of ONE shared pre-screen attempt.
 *
 * Same ResultDetail shape as getMockResult so the detail panels are reused.
 * The gateway enforces recruiter access + the candidate's per-type visibility;
 * a hidden attempt returns 404. The candidate's email is scrubbed server-side.
 */
export async function getRecruiterPreScreenDetail(
  apiCall: ApiCall,
  candidateId: string,
  sessionId: string,
): Promise<ResultDetail> {
  return jsonOrThrow(
    await apiCall(
      `${API_BASE}/api/v1/interview/prescreen/candidate/${candidateId}/sessions/${sessionId}`,
    ),
    'getRecruiterPreScreenDetail',
  );
}

/**
 * Hard-delete one of the calling user's own mocks (recording, transcript,
 * evaluation, audit logs). No quota refund.
 */
export async function deleteMock(
  apiCall: ApiCall,
  sessionId: string,
): Promise<{ deleted: boolean; session_id: string }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/mock/sessions/${sessionId}`, {
      method: 'DELETE',
    }),
    'deleteMock',
  );
}


// ============================================================
// Passport Pre-Screen (candidate self-serve + recruiter read)
// ============================================================

export interface PreScreenTemplate {
  id: string;
  family_key: string;
  title: string;
  description?: string | null;
  type?: string | null;
  difficulty?: string | null;
  duration_minutes?: number | null;
  language?: string | null;
  persona_id?: string | null;
}

export interface PreScreenQuota {
  total: number;
  used: number;
  remaining: number;
  period_start: number;
  period_end: number;
  family_key: string;
  is_admin: boolean;
}

export interface PreScreenStartResponse {
  session: InviteSession;
  raw_token: string;
  invite_url: string;
  quota: PreScreenQuota;
}

export interface PreScreenMine {
  session_id: string;
  family_key: string | null;
  template_title: string | null;
  template_type: string | null;
  language: string | null;
  status: string;
  started_at: number | null;
  completed_at: number | null;
  duration_seconds: number | null;
  overall_score: number | null;
  final_score: number | null;
  recommendation: string | null;
  recruiter_visible: boolean;
  created_at: number;
}

export interface PreScreenRecruiterResult {
  session_id: string;
  family_key: string;
  template_title: string | null;
  template_type: string | null;
  language: string | null;
  completed_at: number | null;
  duration_seconds: number | null;
  overall_score: number | null;
  recommendation: string | null;
}

export async function listPreScreenTemplates(
  apiCall: ApiCall,
  language?: string,
): Promise<{ templates: PreScreenTemplate[] }> {
  const qs = language ? `?language=${encodeURIComponent(language)}` : '';
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/templates${qs}`),
    'listPreScreenTemplates',
  );
}

export async function getPreScreenQuota(
  apiCall: ApiCall,
  familyKey: string,
): Promise<{ quota: PreScreenQuota }> {
  return jsonOrThrow(
    await apiCall(
      `${API_BASE}/api/v1/interview/prescreen/quota?family_key=${encodeURIComponent(familyKey)}`,
    ),
    'getPreScreenQuota',
  );
}

export async function startPreScreen(
  apiCall: ApiCall,
  payload: { family_key?: string; template_id?: string; language?: string; explicit_consent: boolean },
): Promise<PreScreenStartResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'startPreScreen',
  );
}

export async function listMyPreScreens(
  apiCall: ApiCall,
  limit: number = 50,
): Promise<{ prescreens: PreScreenMine[] }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/me?limit=${limit}`),
    'listMyPreScreens',
  );
}

export async function deletePreScreen(
  apiCall: ApiCall,
  sessionId: string,
): Promise<{ deleted: boolean; session_id: string }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/sessions/${sessionId}`, {
      method: 'DELETE',
    }),
    'deletePreScreen',
  );
}

export async function setPreScreenSessionVisibility(
  apiCall: ApiCall,
  sessionId: string,
  visible: boolean,
): Promise<{ session_id: string; recruiter_visible: boolean; family_key: string | null }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/sessions/${sessionId}/visibility`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visible }),
    }),
    'setPreScreenSessionVisibility',
  );
}

export interface PreScreenIntake {
  current_location?: string;
  target_locations?: string;
  seeking?: string;
  availability?: string;
}

/**
 * Save a short optional intake (location / where they want to go / what they're
 * looking for) for a pre-screen attempt. Stored session-scoped on the attempt,
 * never on the candidate profile.
 */
export async function savePreScreenIntake(
  apiCall: ApiCall,
  sessionId: string,
  intake: PreScreenIntake,
): Promise<{ session_id: string; intake: PreScreenIntake }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/sessions/${sessionId}/intake`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(intake),
    }),
    'savePreScreenIntake',
  );
}

export async function getRecruiterPreScreens(
  apiCall: ApiCall,
  candidateId: string,
): Promise<{ prescreens: PreScreenRecruiterResult[] }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/prescreen/candidate/${candidateId}`),
    'getRecruiterPreScreens',
  );
}


// ============================================================
// Recruiter demo (preview the candidate experience)
// ============================================================

export interface DemoStartPayload {
  template_id: string;
  persona_id?: string | null;
  language?: string | null;
  target_difficulty?: string | null;
  target_duration_minutes?: number | null;
  /** Mirrors invite form -- backend maps to target_difficulty. */
  role_seniority?: SeniorityLevel | string | null;
  role_title?: string | null;
}

export interface DemoStartResponse {
  session: InviteSession;
  raw_token: string;
  invite_url: string;
}

/** Demo history item -- same shape as a mock item plus an is_demo marker. */
export interface DemoHistoryItem extends MockHistoryItem {
  is_demo?: boolean;
}

/**
 * Start a recruiter demo of one of the recruiter's templates. The
 * recruiter goes through the exact candidate experience (no email,
 * self-consumed token). Returns an invite_url to navigate to, mirroring
 * the mock flow.
 */
export async function startRecruiterDemo(
  apiCall: ApiCall,
  payload: DemoStartPayload,
): Promise<DemoStartResponse> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites/demo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
    'startRecruiterDemo',
  );
}

/** List the calling recruiter's own demo sessions (for the hub). */
export async function listRecruiterDemos(
  apiCall: ApiCall,
  limit: number = 50,
): Promise<{ demos: DemoHistoryItem[] }> {
  return jsonOrThrow(
    await apiCall(`${API_BASE}/api/v1/interview/invites/demos?limit=${limit}`),
    'listRecruiterDemos',
  );
}


/**
 * Build the WSS URL the browser should connect to.
 * Backend returns a path like /api/v1/sessions/{id}/ws which the gateway
 * proxies. We anchor it to `wss://api.cevee.ai/api/v1/interview` so it
 * passes through the gateway routing rule.
 */
export function buildInterviewWsUrl(sessionId: string): string {
  const wsBase = API_BASE.replace(/^https?:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
  return `${wsBase}/api/v1/interview/sessions/${encodeURIComponent(sessionId)}/ws`;
}
